import React, { useEffect, useMemo, useRef, useState } from 'react';
// Bundled, not fetched: a guest on this page has no internet yet.
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/fraunces/500.css';
import '@fontsource/noto-sans-devanagari/400.css';
import '@fontsource/noto-sans-devanagari/600.css';
import '@fontsource/noto-serif-devanagari/700.css';
import { LANGUAGES, t, languageOf, durationText } from './scenicStrings';
import { plain } from '../plainText';
import { isAppleDevice } from '../appleDevice';
import './ScenicPortal.css';

/**
 * "Scenic" sign-in page — one builder block (type scenic-portal) that is the
 * whole page: a full-bleed photo, the organisation's logo and name, a big
 * place name with a tagline, an EN / हिं / मरा switch, and a card that signs
 * the guest in with their mobile number (WhatsApp or SMS one-time code):
 *
 *   mobile → code → (an ISP: plan and payment) → online
 *
 * A toy-train track over the card shows the step. Everything the guest reads
 * is in their chosen language (scenicStrings.js); the organisation's own
 * words come from the block's props, edited in the builder's Inspector.
 *
 * Live (authContext.scenic, set by Landing.jsx on the hotspot) the card runs
 * the branch's real OTP sign-in — or, for an ISP, the real purchase flow
 * (ispCheckout). Anywhere else — the builder's preview, or the portal opened
 * away from the hotspot — it is a preview: nothing is sent or charged, and the
 * code is 123456.
 */

const PREVIEW_CODE = '123456';
const MOBILE_RE = /^[6-9]\d{9}$/;
const LANG_KEY = 'hotwifi.scenic.lang';

// A colour prop is a token value or a hex; anything else falls back to the default green.
const SAFE_COLOR = /^(#[0-9a-f]{3,8}|var\(--[a-z0-9-]+(,\s*#[0-9a-f]{3,8})?\)|rgba?\([\d\s.,%]+\))$/i;
const DEFAULT_BRAND = '#1d3a2b';

const readLang = (fallback) => {
    try { return languageOf(localStorage.getItem(LANG_KEY) || fallback); } catch { return languageOf(fallback); }
};
const saveLang = (code) => {
    try { localStorage.setItem(LANG_KEY, code); } catch { /* storage blocked */ }
};

const isFreePlan = (plan) => !(Number(plan?.pricing?.amount) > 0);


const locale = (lang) => (lang === 'en' ? 'en-IN' : `${lang}-IN`);
const when = (lang, date) => (date ? new Date(date).toLocaleString(locale(lang), { dateStyle: 'medium', timeStyle: 'short' }) : '');

function bytesText(bytes) {
    const b = Number(bytes) || 0;
    if (b >= 1024 ** 3) return `${(b / 1024 ** 3).toFixed(b >= 10 * 1024 ** 3 ? 0 : 1)} GB`;
    if (b >= 1024 ** 2) return `${Math.round(b / 1024 ** 2)} MB`;
    return `${Math.round(b / 1024)} KB`;
}

/** The time left until `end`, ticking each minute. */
function useTimeLeft(end) {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        if (!end) return undefined;
        const id = setInterval(() => setNow(Date.now()), 30 * 1000);
        return () => clearInterval(id);
    }, [end]);
    if (!end) return null;
    const ms = Math.max(0, new Date(end).getTime() - now);
    const minutes = Math.floor(ms / 60000);
    return { d: Math.floor(minutes / 1440), h: Math.floor((minutes % 1440) / 60), m: minutes % 60 };
}

/** "Free for 1 hour per session" / "Plans from ₹99" — below the button. */
function sessionNote(lang, props, plans) {
    const minutes = Number(props.freeSessionMinutes);
    if (minutes > 0) {
        const validity = minutes % 1440 === 0 ? { duration: minutes / 1440, unit: 'days' }
            : minutes % 60 === 0 ? { duration: minutes / 60, unit: 'hours' }
                : { duration: minutes, unit: 'minutes' };
        return t(lang, 'sessionFree', { duration: durationText(lang, validity) });
    }
    if (!plans?.length) return '';
    const free = plans.find(isFreePlan);
    if (free) return t(lang, 'sessionFree', { duration: durationText(lang, free.validity) });
    const cheapest = Math.min(...plans.map((p) => Number(p.pricing?.amount) || 0));
    return t(lang, 'plansFrom', { amount: cheapest });
}

// ── Icons — inline SVG: icon fonts come from a CDN a guest can't reach yet ──
const WhatsAppIcon = () => (
    <svg viewBox="0 0 24 24" className="sc-icon sc-icon-wa" aria-hidden="true" focusable="false">
        <path d="M12 2.2a9.8 9.8 0 0 0-8.4 14.9L2.2 21.8l4.8-1.3A9.8 9.8 0 1 0 12 2.2Zm0 17.8a8 8 0 0 1-4.1-1.1l-.3-.2-2.9.8.8-2.8-.2-.3A8 8 0 1 1 12 20Zm4.4-6c-.2-.1-1.4-.7-1.7-.8-.2-.1-.4-.1-.5.1l-.8 1c-.1.2-.3.2-.5.1a6.6 6.6 0 0 1-3.3-2.9c-.2-.4.2-.4.7-1.3.1-.2 0-.3 0-.4l-.8-1.8c-.2-.5-.4-.4-.5-.4h-.5a.9.9 0 0 0-.7.3 2.8 2.8 0 0 0-.9 2.1 4.9 4.9 0 0 0 1 2.6 11.2 11.2 0 0 0 4.3 3.8c1.6.7 2.2.7 3 .6a2.6 2.6 0 0 0 1.7-1.2 2.1 2.1 0 0 0 .2-1.2c-.1-.1-.2-.2-.5-.3Z" />
    </svg>
);
const SmsIcon = () => (
    <svg viewBox="0 0 24 24" className="sc-icon" aria-hidden="true" focusable="false">
        <path d="M4 4h16a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H9l-4.3 3.4A.4.4 0 0 1 4 20V5a1 1 0 0 1 1-1Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
        <path d="M8 9h8M8 12.5h5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
);
const WifiIcon = () => (
    <svg viewBox="0 0 24 24" className="sc-icon" aria-hidden="true" focusable="false">
        <path d="M2.5 9a14 14 0 0 1 19 0M5.5 12.2a9.6 9.6 0 0 1 13 0M8.6 15.4a5.2 5.2 0 0 1 6.8 0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <circle cx="12" cy="18.6" r="1.4" fill="currentColor" />
    </svg>
);
const CheckIcon = () => (
    <svg viewBox="0 0 24 24" className="sc-icon" aria-hidden="true" focusable="false">
        <path d="M5 12.5l4.5 4.5L19 7.5" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

/** The little toy train — Matheran's own — riding the track to the current step. */
const ToyTrain = () => (
    <svg viewBox="0 0 44 22" className="sc-train-engine" aria-hidden="true" focusable="false">
        <rect x="1" y="3" width="13" height="12" rx="1.5" fill="#8f2f24" />
        <rect x="3" y="5.5" width="4" height="4" rx="0.8" fill="#f3e7c7" />
        <rect x="8.5" y="5.5" width="4" height="4" rx="0.8" fill="#f3e7c7" />
        <rect x="14" y="7" width="24" height="8" rx="1.5" fill="#2f5d3a" />
        <rect x="14" y="7" width="24" height="2.4" fill="#d9b13b" />
        <rect x="26" y="3.5" width="3" height="4" fill="#2f5d3a" />
        <rect x="38" y="9" width="4.5" height="5" rx="1" fill="#d9b13b" />
        <circle cx="7" cy="17.5" r="2.6" fill="#1b1b1b" />
        <circle cx="19" cy="17.5" r="2.6" fill="#1b1b1b" />
        <circle cx="31" cy="17.5" r="2.6" fill="#1b1b1b" />
    </svg>
);

function TrainTrack({ steps, current, lang }) {
    const index = Math.max(0, steps.indexOf(current));
    const at = steps.length > 1 ? (index / (steps.length - 1)) * 100 : 0;
    const label = { mobile: 'stepMobile', code: 'stepCode', plan: 'stepPlan', online: 'stepOnline' };
    return (
        <div className="sc-track" role="progressbar" aria-valuemin={1} aria-valuemax={steps.length} aria-valuenow={index + 1}
            aria-valuetext={t(lang, label[current])}>
            <div className="sc-track-rail">
                <span className="sc-train" style={{ left: `${at}%` }}><ToyTrain /></span>
            </div>
            <ol className="sc-track-stops">
                {steps.map((step, i) => (
                    <li key={step} className={step === current ? 'is-current' : (i < index ? 'is-done' : '')}
                        style={{ left: `${steps.length > 1 ? (i / (steps.length - 1)) * 100 : 0}%` }}>
                        <span className="sc-stop-mark" />
                        <span className="sc-stop-label">{t(lang, label[step])}</span>
                    </li>
                ))}
            </ol>
        </div>
    );
}

/** Preview-only stand-in for the live flow: the same screens, nothing sent or charged. */
function usePreviewFlow(plans) {
    const [phone, setPhone] = useState('');
    const [channel, setChannel] = useState('whatsapp');
    const [codeSent, setCodeSent] = useState(false);
    const [code, setCode] = useState('');
    const [timer, setTimer] = useState(0);
    const [stage, setStage] = useState('form'); // form | plan | online
    useEffect(() => {
        if (timer <= 0) return undefined;
        const id = setTimeout(() => setTimer((s) => s - 1), 1000);
        return () => clearTimeout(id);
    }, [timer]);
    return {
        live: false,
        mode: plans.length ? 'isp' : 'otp',
        phone, setPhone, channel, setChannel, channels: null, code, setCode, timer,
        codeSent,
        busy: false,
        sendCode: () => { setCodeSent(true); setTimer(30); return true; },
        verify: (errors) => {
            if (code !== PREVIEW_CODE) { errors('errPreviewCode'); return; }
            setStage(plans.length ? 'plan' : 'online');
        },
        changeNumber: () => { setCodeSent(false); setCode(''); setTimer(0); },
        step: stage === 'form' ? (codeSent ? 'code' : 'mobile') : stage,
        plans,
        pay: () => setStage('online'),
        reset: () => { setStage('form'); setCodeSent(false); setCode(''); setTimer(0); },
    };
}

export default function ScenicPortal({ node, portalData = {}, authContext = {}, className = '' }) {
    const props = node?.props || {};
    const live = authContext.scenic?.live ? authContext.scenic : null;
    const plans = useMemo(() => (Array.isArray(portalData.plans) ? portalData.plans : []), [portalData.plans]);
    const preview = usePreviewFlow(plans);
    const flow = live || preview;

    const [lang, setLang] = useState(() => readLang(props.defaultLanguage || 'en'));
    const [agreed, setAgreed] = useState(false);
    const [error, setError] = useState('');
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [selectedPlanId, setSelectedPlanId] = useState(null);
    const firstField = useRef(null);
    const say = (key, vars) => t(lang, key, vars);
    const fail = (key) => setError(say(key));

    const chooseLang = (code) => { setLang(code); saveLang(code); };

    // Organisation words: the block's own per language, else the branch's name.
    const langKey = lang === 'en' ? 'En' : lang === 'hi' ? 'Hi' : 'Mr';
    const orgName = plain(props[`orgName${langKey}`] || props.orgNameEn || portalData.tenantName || portalData.branchName || '');
    const tagline = plain(props[`tagline${langKey}`] || say('tagline'));
    const heroTitle = plain(props.heroTitle || portalData.branchName || '');
    const timeLeft = useTimeLeft(flow.activePlan?.validTill);
    const logo = props.logo || portalData.logo || '';
    const background = props.background || portalData.backgroundImage || '';
    const brand = SAFE_COLOR.test(String(props.brandColor || '')) ? props.brandColor : (portalData.brandColor || DEFAULT_BRAND);

    const channels = (flow.channels && flow.channels.length ? flow.channels : ['whatsapp', 'sms']).filter((c) => c === 'whatsapp' || c === 'sms');
    useEffect(() => {
        if (channels.length && !channels.includes(flow.channel)) flow.setChannel(channels[0]);
    }, [channels.join(','), flow.channel]); // eslint-disable-line react-hooks/exhaustive-deps

    const status = live ? authContext.status : null;
    const stepNow = status?.kind === 'connected' ? 'online' : flow.step;
    const steps = ['mobile', 'code', ...(flow.mode === 'isp' && plans.length ? ['plan'] : []), 'online'];
    const trackStep = ['active', 'verifying'].includes(stepNow) ? (steps.includes('plan') ? 'plan' : 'code') : stepNow;
    const selectedPlan = plans.find((p) => String(p._id) === String(selectedPlanId)) || null;

    useEffect(() => { setError(''); }, [stepNow, lang]);
    // A single free plan and nothing else to choose: connect without a plan screen.
    const autoFree = useRef(false);
    useEffect(() => {
        if (stepNow !== 'plan' || autoFree.current) return;
        if (plans.length === 1 && isFreePlan(plans[0])) {
            autoFree.current = true;
            flow.pay(plans[0], { name: '', email: '' });
        }
    }, [stepNow]); // eslint-disable-line react-hooks/exhaustive-deps

    const onSend = async () => {
        setError('');
        const phone = String(flow.phone || '').replace(/\D/g, '');
        if (!MOBILE_RE.test(phone)) return fail('errMobile');
        if (!agreed) return fail('errTerms');
        await flow.sendCode();
        return undefined;
    };
    const onVerify = async () => {
        setError('');
        if (!/^\d{6}$/.test(String(flow.code || ''))) return fail('errCode');
        await flow.verify(fail);
        return undefined;
    };
    const onPay = () => {
        setError('');
        if (!selectedPlan) return;
        if (!isFreePlan(selectedPlan) && !name.trim()) return fail('errName');
        flow.pay(selectedPlan, { name: name.trim(), email: email.trim() });
    };

    const pageStyle = { '--sc-brand': brand, ...(background ? { '--sc-photo': `url("${String(background).replace(/["\\]/g, '')}")` } : {}) };
    const note = plain(props.sessionNote || sessionNote(lang, props, plans));
    // The credit line is on every page; the block's own value only names the developer.
    const poweredBy = plain(props.poweredBy || '') || 'AsyncPulse Technologies';
    // What the server said went wrong (wrong code, 5xx …), shown where the guest is.
    const serverError = live ? (authContext.loginError || (status?.kind === 'error' ? status.message : '')) : '';
    const shownError = error || serverError;
    // The plan this number has (running, or ended) — offered as "Renew" in the list.
    const ownPlanId = String(flow.activePlan?.planId || flow.lastPlan?.planId || '');

    let body;
    if (status && status.kind !== 'connected' && status.kind !== 'error') {
        // "My Wi-Fi" (back on the portal while connected / after Disconnect): the shared screens.
        body = <StatusInCard status={status} lang={lang} />;
    } else if (stepNow === 'online') {
        body = (
            <div className="sc-online" role="status">
                <span className="sc-online-icon"><CheckIcon /></span>
                <h2 className="sc-title">{say('onlineTitle')}</h2>
                <p className="sc-lead">{live ? say('onlineLead') : say('previewOnline')}</p>
                {live && status?.onContinue && (
                    <button type="button" className="sc-btn" onClick={status.onContinue}>{say('continue')}</button>
                )}
                {!live && <button type="button" className="sc-btn sc-btn-ghost" onClick={preview.reset}>{say('startOver')}</button>}
            </div>
        );
    } else if (stepNow === 'verifying') {
        body = <div className="sc-online" role="status"><span className="sc-spinner" /><p className="sc-lead">{say('checking')}</p></div>;
    } else if (stepNow === 'active') {
        // The guest's dashboard: the plan running now, what is left of it, and Connect.
        const active = flow.activePlan || {};
        const quota = Number(active.dataQuota) || 0;
        const used = Number(active.dataUsed) || 0;
        const usedShare = quota ? Math.min(100, Math.round((used / quota) * 100)) : 0;
        body = (
            <div className="sc-dash">
                <h2 className="sc-title">{say('activeTitle')}</h2>
                <div className="sc-dash-plan">
                    <span className="sc-dash-plan-name">{active.name}</span>
                    {active.downloadSpeed && <span className="sc-dash-chip">{say('dashMbps', { speed: Math.round(active.downloadSpeed / 1024) || 1 })}</span>}
                </div>
                <dl className="sc-rows">
                    <div><dt>{say('dashValidTill')}</dt><dd>{when(lang, active.validTill)}</dd></div>
                    {timeLeft && (
                        <div><dt>{say('dashTimeLeft')}</dt><dd>{timeLeft.d > 0 ? say('dashLeft', timeLeft) : say('dashLeftHours', timeLeft)}</dd></div>
                    )}
                    <div>
                        <dt>{say('dashData')}</dt>
                        <dd>{quota ? say('dashDataOf', { used: bytesText(used), quota: bytesText(quota) }) : `${bytesText(used)} · ${say('dashUnlimited')}`}</dd>
                    </div>
                </dl>
                {quota > 0 && (
                    <div className="sc-meter" role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={usedShare} aria-label={say('dashData')}>
                        <span style={{ width: `${usedShare}%` }} />
                    </div>
                )}
                {shownError && <div className="sc-error" role="alert">{shownError}</div>}
                <button type="button" className="sc-btn" disabled={flow.busy} onClick={flow.connectActive}>{flow.busy ? say('connecting') : say('connect')}</button>
                <button type="button" className="sc-btn sc-btn-ghost" onClick={() => flow.setIspStep?.('select')}>{say('renewOrChange')}</button>
            </div>
        );
    } else if (stepNow === 'plan') {
        const active = flow.activePlan;
        const renewing = selectedPlan && active && String(selectedPlan._id) === String(active.planId);
        body = (
            <>
                <h2 className="sc-title">{say('planTitle')}</h2>
                {flow.lastPlan && !active
                    ? <div className="sc-expired" role="status">{say('expiredBanner', { plan: flow.lastPlan.name, date: when(lang, flow.lastPlan.endedAt) })}</div>
                    : <p className="sc-lead">{live ? say('planLead') : say('previewPay')}</p>}
                {plans.length === 0 ? <p className="sc-note">{say('noPlans')}</p> : (
                    <div className="sc-plans" role="radiogroup" aria-label={say('planTitle')}>
                        {plans.map((plan) => {
                            const chosen = String(plan._id) === String(selectedPlanId);
                            return (
                                <button key={plan._id} type="button" role="radio" aria-checked={chosen}
                                    className={`sc-plan ${chosen ? 'is-chosen' : ''}`} onClick={() => setSelectedPlanId(plan._id)}>
                                    <span className="sc-plan-name">
                                        {plan.name}
                                        {String(plan._id) === ownPlanId && <span className="sc-plan-badge">{say('yourPlan')}</span>}
                                    </span>
                                    <span className="sc-plan-price">{isFreePlan(plan) ? say('free') : `₹${plan.pricing.amount}`}</span>
                                    <span className="sc-plan-meta">
                                        {durationText(lang, plan.validity)}
                                        {plan.bandwidth?.downloadSpeed ? ` · ${say('dashMbps', { speed: Math.round(plan.bandwidth.downloadSpeed / 1024) || 1 })}` : ''}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                )}
                {renewing && (
                    <p className="sc-hint">{say('renewAdds', { duration: durationText(lang, selectedPlan.validity), date: when(lang, active.validTill) })}</p>
                )}
                {selectedPlan && !isFreePlan(selectedPlan) && (
                    <>
                        <label className="sc-label" htmlFor="sc-name">{say('nameLabel')}</label>
                        <input id="sc-name" className="sc-input" value={name} onChange={(e) => setName(e.target.value)} placeholder={say('namePlaceholder')} autoComplete="name" />
                        <label className="sc-label" htmlFor="sc-email">{say('emailLabel')}</label>
                        <input id="sc-email" className="sc-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
                    </>
                )}
                {shownError && <div className="sc-error" role="alert">{shownError}</div>}
                {selectedPlan && (
                    <button type="button" className="sc-btn" disabled={flow.busy} onClick={onPay}>
                        {isFreePlan(selectedPlan) ? say('connectFree')
                            : renewing ? say('renew', { amount: selectedPlan.pricing.amount })
                                : say('pay', { amount: selectedPlan.pricing.amount })}
                    </button>
                )}
                {active && <button type="button" className="sc-link-btn" onClick={() => flow.setIspStep?.('active')}>{say('activeTitle')}</button>}
            </>
        );
    } else if (stepNow === 'code') {
        body = (
            <>
                <h2 className="sc-title">{say('codeTitle')}</h2>
                <p className="sc-lead">{say('codeLead', { mobile: flow.phoneHint || String(flow.phone || '').replace(/\D/g, ''), channel: say(flow.channel === 'sms' ? 'sms' : 'whatsapp') })}</p>
                {flow.preLogin && <p className="sc-prelogin" role="status">{say(isAppleDevice() ? 'preLoginNoteApple' : 'preLoginNote', { minutes: flow.preLogin.minutes })}</p>}
                <label className="sc-label" htmlFor="sc-code">{say('codeLabel')}</label>
                <input
                    id="sc-code" ref={firstField} className="sc-input sc-code-input" inputMode="numeric" autoComplete="one-time-code"
                    maxLength={6} value={flow.code || ''} placeholder={say('codePlaceholder')}
                    onChange={(e) => flow.setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onVerify(); } }}
                />
                {shownError && <div className="sc-error" role="alert">{shownError}</div>}
                <button type="button" className="sc-btn" disabled={flow.busy} onClick={onVerify}>{flow.busy ? say('connecting') : say('verify')}</button>
                <div className="sc-code-actions">
                    {flow.timer > 0
                        ? <span className="sc-note">{say('resendIn', { seconds: flow.timer })}</span>
                        : <button type="button" className="sc-link-btn" onClick={() => flow.sendCode()}>{say('resend')}</button>}
                    <button type="button" className="sc-link-btn" onClick={flow.changeNumber}>{say('changeNumber')}</button>
                </div>
            </>
        );
    } else {
        body = (
            <>
                <h2 className="sc-title">{say('mobileTitle')}</h2>
                <p className="sc-lead">{say('mobileLead')}</p>
                {channels.length > 1 && (
                    <>
                        <div className="sc-label" id="sc-sendby">{say('sendBy')}</div>
                        <div className="sc-channels" role="radiogroup" aria-labelledby="sc-sendby">
                            {channels.map((c) => (
                                <button key={c} type="button" role="radio" aria-checked={flow.channel === c}
                                    className={`sc-channel ${flow.channel === c ? 'is-chosen' : ''}`} onClick={() => flow.setChannel(c)}>
                                    {c === 'whatsapp' ? <WhatsAppIcon /> : <SmsIcon />}
                                    <span>{say(c)}</span>
                                </button>
                            ))}
                        </div>
                    </>
                )}
                {flow.channel === 'whatsapp' && <p className="sc-hint">{say('whatsappHint')}</p>}
                <label className="sc-label" htmlFor="sc-mobile">{say('mobileLabel')}</label>
                <div className="sc-phone">
                    <span className="sc-phone-cc">+91</span>
                    <input
                        id="sc-mobile" ref={firstField} className="sc-phone-input" type="tel" inputMode="numeric" autoComplete="tel-national"
                        maxLength={10} value={flow.phone || ''} placeholder={say('mobilePlaceholder')}
                        onChange={(e) => flow.setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onSend(); } }}
                    />
                </div>
                <label className="sc-agree">
                    <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
                    <span>
                        {say('agreePrefix')}{' '}
                        <a href="#terms" onClick={(e) => { e.preventDefault(); authContext.setShowTermsModal?.(true); }}>{say('termsLink')}</a>
                        {say('agreeSuffix') ? ` ${say('agreeSuffix')}` : ''}
                    </span>
                </label>
                {shownError && <div className="sc-error" role="alert">{shownError}</div>}
                <button type="button" className="sc-btn" disabled={flow.busy} onClick={onSend}>{say('sendCode')}</button>
            </>
        );
    }

    return (
        <div
            className={`sc-page ${background ? 'has-photo' : ''} ${className}`.trim()} style={pageStyle} lang={lang}
            // The page sits inside the portal's sign-in <form>: Enter in a field here must
            // not submit that form (each field handles Enter itself).
            onKeyDown={(e) => { if (e.key === 'Enter' && e.target.tagName === 'INPUT') e.preventDefault(); }}
        >
            <div className="sc-photo" aria-hidden="true" />
            <header className="sc-header">
                <div className="sc-org">
                    {logo && <img className="sc-org-logo" src={logo} alt="" />}
                    {orgName && <span className="sc-org-name">{orgName}</span>}
                </div>
                {props.showLanguages !== 'no' && (
                    <div className="sc-langs" role="group" aria-label="Language">
                        {LANGUAGES.map((l) => (
                            <button key={l.code} type="button" className={`sc-lang ${lang === l.code ? 'is-active' : ''}`}
                                aria-pressed={lang === l.code} onClick={() => chooseLang(l.code)}>
                                {l.label}
                            </button>
                        ))}
                    </div>
                )}
            </header>

            <div className="sc-hero">
                {heroTitle && <h1 className="sc-hero-title">{heroTitle}</h1>}
                {tagline && <p className="sc-hero-tagline"><WifiIcon />{tagline}</p>}
            </div>

            <section className="sc-card" aria-label={say('mobileTitle')}>
                {!live && <div className="sc-preview" role="note">{say('preview')}</div>}
                {props.showTrack !== 'no' && <TrainTrack steps={steps} current={trackStep} lang={lang} />}
                {body}
                {note && stepNow !== 'online' && <p className="sc-note sc-note-center">{note}</p>}
                {poweredBy && <p className="sc-powered">{say('poweredBy', { name: poweredBy })}</p>}
            </section>
        </div>
    );
}

/** The "My Wi-Fi" screens (online / disconnected) inside the card, in this page's words. */
function StatusInCard({ status, lang }) {
    if (status.kind === 'online') {
        return (
            <div className="sc-online" role="status">
                <span className="sc-online-icon"><CheckIcon /></span>
                <h2 className="sc-title">{t(lang, 'onlineTitle')}</h2>
                <dl className="sc-rows">
                    {(status.details || []).filter((r) => r?.value).map((r) => <div key={r.label}><dt>{r.label}</dt><dd>{r.value}</dd></div>)}
                </dl>
                {status.onContinue && <button type="button" className="sc-btn" onClick={status.onContinue}>{t(lang, 'continue')}</button>}
                {status.onDisconnect && (
                    <button type="button" className="sc-btn sc-btn-ghost" disabled={status.busy} onClick={status.onDisconnect}>Disconnect</button>
                )}
            </div>
        );
    }
    return (
        <div className="sc-online" role="status">
            <dl className="sc-rows">
                {(status.details || []).filter((r) => r?.value).map((r) => <div key={r.label}><dt>{r.label}</dt><dd>{r.value}</dd></div>)}
            </dl>
            {status.onConnectAgain && <button type="button" className="sc-btn" onClick={status.onConnectAgain}>{t(lang, 'connect')}</button>}
        </div>
    );
}
